import React, { useEffect, useState } from "react";
import { FormMode } from "constant";
import { Modal, useTranslations } from "@gf-easy-8/grmnext-foundation";
import { useDispatch } from "react-redux";

import useStatusBar from "provider/StatusBarProvider";
import useSnackBar from "provider/SnackBarProvider";
import { AppDispatch } from "types";
import UserDetails from "./UserDetails";
import ProductAccessDetails from "./ProductAccessDetails";
import { getUserV2, createUserV2 } from "./actions";
import { getUsersV2 } from "../actions";
import { UserFormData } from "./type";

const UserFormModalV2 = ({
  show,
  onClose,
  formMode,
  user,
}: {
  show: boolean;
  onClose: () => void;
  formMode: number;
  user: UserFormData;
}) => {
  const form = useForm(formMode);
  const { getTranslation } = useTranslations();
  const dispatch: AppDispatch = useDispatch();
  const { statusBarContext } = useStatusBar();
  const { setStatusBarToLoading, setStatusBarToHealthy, setStatusBarToError } =
    statusBarContext.setStatusBar;
  const { snackBarContext } = useSnackBar();
  const { pushSuccessMessage, pushErrorMessage } = snackBarContext.modify;

  const initValuesForm: UserFormData = {
    employeeId: "",
    emailAddress: "",
    firstName: "",
    lastName: "",
    department: "",
    userProducts: [],
    products: [],
  };

  const intiFormErrors: UserFormData = {
    employeeId: "",
    emailAddress: "",
    firstName: "",
    lastName: "",
    department: "",
    productsError: "",
  };

  const [formData, setFormData] = useState(initValuesForm);
  const [formDataErrors, setFormDataErrors] = useState(intiFormErrors);
  const [showProductAccess, setShowProductAccess] = useState(false);
  const [modalTitle, setModalTitle] = useState(
    getTranslation("USER_MANAGEMENT:USER_DETAILS")
  );
  const [isUserDetailsLoading, setIsUserDetailsLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(-1);
  const [hasPressedSave, setHasPressedSave] = useState<boolean>(false);
  const [refreshUser, setRefreshUser] = useState<number>(new Date().getTime());

  const validateCreateUserForm = (formData: UserFormData) => {
    let isValid = true;
    const newFormDataErrors = { ...intiFormErrors };
    type FormDataFields =
      | "employeeId"
      | "emailAddress"
      | "firstName"
      | "lastName"
      | "department"
      | "productsError";
    const requiredFields: FormDataFields[] = [
      "employeeId",
      "emailAddress",
      "firstName",
      "lastName",
      "department",
      "productsError",
    ];

    requiredFields.map((field: FormDataFields) => {
      if (formData[field] === "") {
        isValid = false;
        newFormDataErrors[field] = getTranslation(
          "USER_MANAGEMENT:FIELD_IS_REQUIRED"
        );
      }
    });
    if (formData.products && formData.products.length == 0) {
      isValid = false;
      newFormDataErrors["productsError"] = getTranslation(
        "USER_MANAGEMENT:FIELD_IS_REQUIRED"
      );
    }
    setFormDataErrors(newFormDataErrors);
    return isValid;
  };

  useEffect(() => {
    if (showProductAccess) {
      setModalTitle(getTranslation("USER_MANAGEMENT:PRODUCT_ACCESS_DETAILS"));
    } else {
      setModalTitle(getTranslation("USER_MANAGEMENT:USER_DETAILS"));
    }
  }, [showProductAccess]);

  useEffect(() => {
    setIsUserDetailsLoading(true);
    setStatusBarToLoading();
    if (formMode === FormMode.Edit) {
      dispatch(getUserV2(user.emailAddress))
        .then((userResp: UserFormData) => {
          setFormData(userResp);
          setIsUserDetailsLoading(false);
          setStatusBarToHealthy();
        })
        .catch(() => {
          pushErrorMessage(getTranslation("USER_MANAGEMENT:USER_ERROR"));
          setIsUserDetailsLoading(false);
          setStatusBarToError();
          dispatch(getUsersV2());
          closeModal();
        });
    } else {
      setFormData(initValuesForm);
      setIsUserDetailsLoading(false);
      setStatusBarToHealthy();
    }
  }, [formMode, user, refreshUser]);

  useEffect(() => {
    if (formMode === FormMode.Add && hasPressedSave) {
      validateCreateUserForm(formData);
    }
  }, [formData]);

  const onChangeForm = (id: string, value: string | number) => {
    let email = {};
    if (id === "employeeId") {
      email = {
        emailAddress: value !== "" ? `${value}@grundfos.com` : "",
      };
    }
    setFormData({ ...formData, [id]: value, ...email });
  };

  const closeModal = () => {
    onClose();
    setTimeout(() => {
      setHasPressedSave(false);
      setFormData(initValuesForm);
      setFormDataErrors(intiFormErrors);
      setShowProductAccess(false);
    }, 1000);
  };

  const onCreateUser = () => {
    const isValid = validateCreateUserForm(formData);
    setHasPressedSave(true);
    if (isValid) {
      setIsUserDetailsLoading(true);
      dispatch(createUserV2(formData))
        .then((response: any) => {
          if (response && response.isFailure) {
            pushErrorMessage(
              getTranslation("USER_MANAGEMENT:USER_CREATE_ERROR")
            );
          } else {
            pushSuccessMessage(getTranslation("USER_MANAGEMENT:USER_CREATED"));
            dispatch(getUsersV2());
            closeModal();
          }
          setIsUserDetailsLoading(false);
        })
        .catch(() => {
          pushErrorMessage(getTranslation("USER_MANAGEMENT:USER_CREATE_ERROR"));
          setIsUserDetailsLoading(false);
        });
    }
  };

  return (
    <Modal
      title={modalTitle}
      fullScreenOnMobile={false}
      show={show}
      close={() => closeModal()}
      ok={() => onCreateUser()}
      showFooter={
        !isUserDetailsLoading && !showProductAccess && formMode === FormMode.Add
      }
      okMessage="Save"
    >
      {!showProductAccess ? (
        <UserDetails
          user={user}
          form={form}
          formData={formData}
          setFormData={setFormData}
          formDataErrors={formDataErrors}
          onChangeForm={onChangeForm}
          setShowProductAccess={setShowProductAccess}
          isLoading={isUserDetailsLoading}
          setSelectedProduct={setSelectedProduct}
          setRefreshUser={setRefreshUser}
        />
      ) : (
        <ProductAccessDetails
          user={user}
          form={form}
          formData={formData}
          setFormData={setFormData}
          setShowProductAccess={setShowProductAccess}
          selectedProduct={selectedProduct}
          setSelectedProduct={setSelectedProduct}
          setRefreshUser={setRefreshUser}
        />
      )}
    </Modal>
  );
};

const useForm = (formMode: number) => {
  const [disabled, setDisabled] = useState(false);
  const [mode, setMode] = useState(FormMode.View);

  useEffect(() => {
    if (formMode === FormMode.Add) {
      setDisabled(false);
      setMode(FormMode.Add);
    } else if (formMode === FormMode.Edit) {
      setDisabled(true);
      setMode(FormMode.Edit);
    }
  }, [formMode]);

  return { mode, disabled };
};

export default UserFormModalV2;
